/**
 * The brand's deposit (specification §4.3): the budget goes into the escrow with the exact
 * `createCampaign` parameters the manifest fixes, from the brand's wallet.
 *
 * The escrow takes the budget from the wallet, so the wallet first approves the escrow for
 * exactly the budget. A wallet that executes several calls atomically (EIP-5792, such as Base
 * Account) receives the approval and the deposit as one batch: the brand confirms once, and an
 * approval never stays without its deposit. Other wallets confirm the two transactions one after
 * the other, and an approval left from an earlier attempt is reused.
 *
 * Before the wallet is asked anything, the deposit is checked. It must come from the request's own
 * wallet, which the manifest names as the brand, the campaign's start must not have passed, and
 * the wallet must hold the budget. The deposit transaction is also simulated before it is sent,
 * so that a refusal by the escrow is explained instead of costing gas.
 *
 * Once the wallet has sent the deposit, it may land whatever happens next. The page may lose the
 * connection or be closed before the transaction is mined. The page is told at that moment, and
 * this browser keeps the deposit (see `rememberSentDeposit`) until the request shows what became
 * of it, so that it never offers a second deposit meanwhile.
 */

import { morcastEscrowAbi } from "@morcast/protocol";
import { useMemo, useSyncExternalStore } from "react";
import {
  type Address,
  BaseError,
  ContractFunctionRevertedError,
  decodeEventLog,
  erc20Abi,
  type Hex,
  UserRejectedRequestError,
} from "viem";
import type { Config } from "wagmi";
import {
  getBlock,
  getCapabilities,
  getConnection,
  readContract,
  sendCalls,
  simulateContract,
  switchChain,
  waitForCallsStatus,
  waitForTransactionReceipt,
  writeContract,
} from "wagmi/actions";

import { formatAmount } from "../format.js";
import type { CampaignRequest, Deposit } from "./requests.js";

/** Where a deposit in progress stands. */
export type DepositStep =
  | "switching-chain"
  | "confirm-batch"
  | "confirm-approval"
  | "approving"
  | "confirm-deposit"
  | "depositing";

/** What each step asks of the brand or tells it. */
export const DEPOSIT_STEP_MESSAGES: Record<DepositStep, string> = {
  "switching-chain": "Switch your wallet to the campaign's chain…",
  "confirm-batch": "Confirm the approval and the deposit in your wallet…",
  "confirm-approval": "Confirm the approval of the budget in your wallet…",
  approving: "Approving the budget…",
  "confirm-deposit": "Confirm the deposit in your wallet…",
  depositing: "Depositing…",
};

/** A deposit the escrow has recorded. */
export interface MadeDeposit {
  /** The transaction that created the campaign. Null if a wallet's batch reported no receipt. */
  transactionHash: Hex | null;

  /**
   * The escrow's ID of the new campaign, read from its `CampaignCreated` event. `undefined` if a
   * wallet's receipts leave the event out. The worker records the campaign either way.
   */
  campaignId: bigint | undefined;
}

/**
 * A deposit that cannot go ahead, or that failed for certain. Either way nothing was deposited,
 * so the brand may try again. The message is for the brand.
 */
export class DepositError extends Error {
  override name = "DepositError";
}

/** The request fields a deposit needs. */
export type DepositRequest = Pick<CampaignRequest, "brand" | "deposit" | "token">;

/**
 * Makes the deposit of an accepted request from the connected wallet, which must be the
 * request's own: the manifest names it as the brand, and a deposit from any other wallet would
 * fund nothing.
 *
 * @param onStep Called as the deposit progresses, so the page can tell the brand what happens.
 * @param onSent Called as soon as the wallet has sent the deposit, with its transaction, or with
 *               null for a batch, whose transaction the wallet reports only once it is mined.
 *               From then on the deposit may land however this call ends.
 * @throws DepositError when a check fails or the deposit failed on-chain, so that nothing was
 *         deposited. Wallet and chain errors otherwise, which after `onSent` leave the deposit's
 *         fate open. Either way, `describeDepositError` gives the message for the brand.
 */
export async function makeDeposit(
  config: Config,
  request: DepositRequest,
  onStep: (step: DepositStep) => void,
  onSent: (transactionHash: Hex | null) => void = () => {},
): Promise<MadeDeposit> {
  const { deposit } = request;
  if (!deposit) {
    throw new DepositError("The request has no deposit parameters.");
  }
  const { address: account, chainId } = getConnection(config);
  if (!account) {
    throw new DepositError("Connect your wallet to deposit.");
  }
  if (account.toLowerCase() !== request.brand.toLowerCase()) {
    throw new DepositError(
      `The deposit must come from ${request.brand}, the wallet that sent this request. Switch your wallet to that account first.`,
    );
  }
  if (chainId !== deposit.chainId) {
    onStep("switching-chain");
    await switchChain(config, { chainId: deposit.chainId });
  }

  await checkDeposit(config, request, deposit, account);
  const receipts = (await executesAtomically(config, deposit.chainId))
    ? await depositInOneBatch(config, deposit, onStep, onSent)
    : await depositStepByStep(config, deposit, account, onStep, onSent);
  return createdCampaign(receipts, deposit);
}

/** Checks the deadline and the wallet's balance against the chain. */
async function checkDeposit(
  config: Config,
  request: DepositRequest,
  deposit: Deposit,
  account: Address,
): Promise<void> {
  const [block, balance] = await Promise.all([
    getBlock(config, { chainId: deposit.chainId }),
    readContract(config, {
      chainId: deposit.chainId,
      address: deposit.token as Address,
      abi: erc20Abi,
      functionName: "balanceOf",
      args: [account],
    }),
  ]);
  // The escrow refuses a deposit in a block at or after the start.
  if (block.timestamp >= BigInt(deposit.startAt)) {
    throw new DepositError(
      "The campaign's start has passed, so the escrow no longer accepts its deposit.",
    );
  }
  if (balance < BigInt(deposit.budget)) {
    throw new DepositError(
      `Your wallet holds ${formatAmount(balance.toString(), request.token)}, but the deposit needs ${formatAmount(deposit.budget, request.token)}.`,
    );
  }
}

/**
 * Whether the wallet executes a batch of calls atomically on the chain as it is now. A wallet
 * that is only ready to (status "ready") would first upgrade the account to a smart account,
 * which a deposit should not start. It gets the transactions one by one instead.
 */
async function executesAtomically(config: Config, chainId: number): Promise<boolean> {
  try {
    const capabilities = await getCapabilities(config, { chainId });
    return capabilities.atomic?.status === "supported";
  } catch {
    // Wallets without EIP-5792 refuse the request.
    return false;
  }
}

/** The approval of exactly the budget. */
function approveCall(deposit: Deposit) {
  return {
    to: deposit.token as Address,
    abi: erc20Abi,
    functionName: "approve",
    args: [deposit.escrow as Address, BigInt(deposit.budget)],
  } as const;
}

/** The deposit itself, with the manifest's parameters. */
function createCampaignCall(deposit: Deposit) {
  return {
    to: deposit.escrow as Address,
    abi: morcastEscrowAbi,
    functionName: "createCampaign",
    args: [
      deposit.token as Address,
      BigInt(deposit.budget),
      BigInt(deposit.target),
      BigInt(deposit.startAt),
      BigInt(deposit.endAt),
      deposit.manifestHash as Hex,
    ],
  } as const;
}

/** A mined transaction's logs, as both receipt kinds give them. */
interface Receipt {
  transactionHash: Hex;
  logs: readonly { address: Hex; data: Hex; topics: readonly Hex[] }[];
}

/** Approval and deposit as one atomic batch. Both happen, or neither does. */
async function depositInOneBatch(
  config: Config,
  deposit: Deposit,
  onStep: (step: DepositStep) => void,
  onSent: (transactionHash: Hex | null) => void,
): Promise<readonly Receipt[]> {
  onStep("confirm-batch");
  const { id } = await sendCalls(config, {
    chainId: deposit.chainId,
    forceAtomic: true,
    calls: [approveCall(deposit), createCampaignCall(deposit)],
  });
  onSent(null);
  onStep("depositing");
  const { status, receipts = [] } = await waitForCallsStatus(config, { id });
  // An atomic batch that failed changed nothing but the gas paid. Any other status leaves the
  // batch's fate unknown, so it is no DepositError. The page keeps awaiting the deposit.
  if (status === "failure") {
    throw new DepositError(
      "The wallet could not complete the deposit. Nothing was taken from your wallet.",
    );
  }
  if (status !== "success") {
    throw new Error(`The wallet reported the deposit's batch as ${status ?? "unknown"}.`);
  }
  return receipts;
}

/** The approval, when the escrow's allowance is short of the budget, then the deposit. */
async function depositStepByStep(
  config: Config,
  deposit: Deposit,
  account: Address,
  onStep: (step: DepositStep) => void,
  onSent: (transactionHash: Hex | null) => void,
): Promise<readonly Receipt[]> {
  const { chainId } = deposit;
  const approve = approveCall(deposit);
  const allowance = await readContract(config, {
    chainId,
    address: approve.to,
    abi: erc20Abi,
    functionName: "allowance",
    args: [account, deposit.escrow as Address],
  });
  if (allowance < BigInt(deposit.budget)) {
    onStep("confirm-approval");
    const hash = await writeContract(config, {
      chainId,
      address: approve.to,
      abi: approve.abi,
      functionName: approve.functionName,
      args: approve.args,
    });
    onStep("approving");
    await mined(config, chainId, hash);
  }

  const create = createCampaignCall(deposit);
  const { request } = await simulateContract(config, {
    chainId,
    account,
    address: create.to,
    abi: create.abi,
    functionName: create.functionName,
    args: create.args,
  });
  onStep("confirm-deposit");
  const hash = await writeContract(config, request);
  onSent(hash);
  onStep("depositing");
  return [await mined(config, chainId, hash)];
}

/**
 * Waits for a transaction and fails if it reverted. Waiting may also end without an answer, after
 * viem's timeout or on a lost connection. That error leaves the transaction's fate open.
 */
async function mined(config: Config, chainId: number, hash: Hex): Promise<Receipt> {
  const receipt = await waitForTransactionReceipt(config, { chainId, hash });
  if (receipt.status !== "success") {
    throw new DepositError(`Transaction ${hash} failed on-chain.`);
  }
  return receipt;
}

/**
 * The campaign the deposit created, from the escrow's `CampaignCreated` event. The deposit has
 * succeeded by now, so a missing event or receipt never turns it into a failure. That would
 * invite the brand to deposit a second time.
 */
function createdCampaign(receipts: readonly Receipt[], deposit: Deposit): MadeDeposit {
  for (const receipt of receipts) {
    for (const log of receipt.logs) {
      if (log.address.toLowerCase() !== deposit.escrow.toLowerCase()) {
        continue;
      }
      const event = decodeEscrowEvent(log);
      if (
        event?.eventName === "CampaignCreated" &&
        event.args.manifestHash.toLowerCase() === deposit.manifestHash.toLowerCase()
      ) {
        return { campaignId: event.args.id, transactionHash: receipt.transactionHash };
      }
    }
  }
  return { campaignId: undefined, transactionHash: receipts.at(-1)?.transactionHash ?? null };
}

/** An escrow log decoded, or `undefined` for one the ABI does not describe. */
function decodeEscrowEvent(log: Receipt["logs"][number]) {
  try {
    return decodeEventLog({
      abi: morcastEscrowAbi,
      data: log.data,
      topics: log.topics as [Hex, ...Hex[]],
    });
  } catch {
    return undefined;
  }
}

/** Messages for the escrow's refusals that a brand can meet. */
const ESCROW_REFUSALS: Record<string, string> = {
  CampaignStarted: "The campaign's start has passed, so the escrow no longer accepts its deposit.",
  CreationPaused: "The escrow has paused new campaigns for now. Try again later.",
  UnsupportedToken: "The escrow no longer accepts this token, so it refuses the deposit.",
  DepositMismatch:
    "The token transfer delivered less than the budget, so the escrow refused the deposit.",
  InvalidBudget: "The deposit parameters break the escrow's rules, so it refuses the deposit.",
  InvalidTarget: "The deposit parameters break the escrow's rules, so it refuses the deposit.",
  InvalidSchedule: "The deposit parameters break the escrow's rules, so it refuses the deposit.",
};

/** The message for the brand about a deposit that failed. */
export function describeDepositError(error: unknown): string {
  if (error instanceof DepositError) {
    return error.message;
  }
  if (error instanceof BaseError) {
    if (error.walk((cause) => cause instanceof UserRejectedRequestError)) {
      return "You declined in your wallet, so nothing was sent.";
    }
    const revert = error.walk((cause) => cause instanceof ContractFunctionRevertedError);
    const refusal =
      revert instanceof ContractFunctionRevertedError
        ? ESCROW_REFUSALS[revert.data?.errorName ?? ""]
        : undefined;
    return refusal ?? `The deposit failed. ${error.shortMessage}`;
  }
  return "The deposit failed. Please try again.";
}

/** A deposit this browser sent, kept until the request shows what became of it. */
export interface SentDeposit {
  /** Its transaction. Null for a batch whose transaction the wallet has not reported. */
  transactionHash: Hex | null;
  /** The new campaign's ID, when the receipts showed it. */
  campaignId: string | null;
  /** When the wallet sent it, in milliseconds since the epoch. */
  sentAt: number;
  /**
   * Set while the deposit is not known to be mined. The wallet sent it, but the page has not
   * seen it on-chain yet, because it is still waiting or stopped waiting without an answer.
   */
  unconfirmed?: true;
}

/**
 * How long a sent deposit is awaited before the page asks the brand to check it: long enough for
 * a congested chain, short enough that a deposit that never went through does not block another.
 */
export const RECORDING_PATIENCE_MS = 30 * 60 * 1000;

const SENT_DEPOSITS_KEY = "morcast.sentDeposits";

/**
 * Sent deposits kept in memory, as JSON, once storage refused them (private browsing, a full
 * quota). They then last as long as the page.
 */
let unstored: string | null = null;

/** Components that show a sent deposit, told when one is remembered or forgotten. */
const listeners = new Set<() => void>();

/** The stored sent deposits as JSON, by request ID. Null when there are none. */
function storedJson(): string | null {
  if (unstored !== null) {
    return unstored;
  }
  try {
    return localStorage.getItem(SENT_DEPOSITS_KEY);
  } catch {
    return null;
  }
}

function parseSentDeposits(json: string | null): Record<number, SentDeposit> {
  try {
    return JSON.parse(json ?? "{}") ?? {};
  } catch {
    return {};
  }
}

function writeSentDeposits(all: Record<number, SentDeposit>): void {
  const json = JSON.stringify(all);
  try {
    localStorage.setItem(SENT_DEPOSITS_KEY, json);
  } catch {
    unstored = json;
  }
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Remembers a sent deposit, so that the page, even reloaded, shows it instead of offering a
 * second deposit while Morcast has not yet recorded it. Remembering it again replaces it.
 */
export function rememberSentDeposit(requestId: number, sent: SentDeposit): void {
  writeSentDeposits({ ...parseSentDeposits(storedJson()), [requestId]: sent });
}

/** The deposit this browser sent for a request, if any. */
export function sentDepositOf(requestId: number): SentDeposit | undefined {
  return parseSentDeposits(storedJson())[requestId];
}

/** Forgets a request's sent deposit: the request shows what became of it, or it never landed. */
export function forgetSentDeposit(requestId: number): void {
  const all = parseSentDeposits(storedJson());
  if (requestId in all) {
    delete all[requestId];
    writeSentDeposits(all);
  }
}

/** Follows remembered and forgotten deposits, in this tab and, through storage, in others. */
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

/** The deposit this browser sent for a request, kept current as it is remembered or forgotten. */
export function useSentDeposit(requestId: number): SentDeposit | undefined {
  const json = useSyncExternalStore(subscribe, storedJson);
  return useMemo(() => parseSentDeposits(json)[requestId], [json, requestId]);
}

/** Whether a sent deposit is still awaited at `now`: sent recently and not yet recorded. */
export function isAwaited(sent: SentDeposit | undefined, now = Date.now()): boolean {
  return sent !== undefined && now - sent.sentAt < RECORDING_PATIENCE_MS;
}

/** Whether a request's sent deposit is still awaited: sent recently and not yet recorded. */
export function awaitsRecording(request: Pick<CampaignRequest, "id" | "status">): boolean {
  return request.status === "accepted" && isAwaited(sentDepositOf(request.id));
}
